import React from 'react';
import { Sparkles, ArrowUpRight, ArrowRight } from 'lucide-react';

export default function Momentum({ 
  momentumMetrics, 
  learningRhythm, 
  momentumInsight, 
  hoveredDay, 
  onHoverDay 
}) {
  return (
    <div className="bg-white rounded-[22px] p-6" style={{ boxShadow: '0 2px 6px rgba(0,0,0,0.06)' }}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold tracking-tight" style={{ color: '#111111' }}>Your Momentum</h3>
        {momentumMetrics.hasEnoughData && momentumMetrics.weekDeltaText !== 'No items yet' && (
          <div 
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
            style={{
              backgroundColor: '#F7F7F8',
              color: momentumMetrics.weekDeltaText.startsWith('From 0')
                ? '#7C3AED'
                : momentumMetrics.weekDeltaPercent >= 0 
                  ? '#7C3AED' 
                  : '#999999'
            }}
          >
            {momentumMetrics.weekDeltaText.startsWith('From 0') ? (
              <Sparkles size={10} />
            ) : momentumMetrics.weekDeltaPercent >= 0 ? (
              <ArrowUpRight size={10} />
            ) : (
              <ArrowRight size={10} className="rotate-45" />
            )}
            {momentumMetrics.weekDeltaPercent >= 0 ? '+' : '−'}{Math.abs(momentumMetrics.weekDeltaPercent)}%
          </div>
        )}
      </div>
      <div className="mb-2">
        <div className="text-3xl font-semibold mb-1" style={{ color: '#111111' }}>
          {momentumMetrics.totalItemsThisWeek || learningRhythm?.weeklyCount || 0}
        </div>
        <div className="text-[12px] font-medium mb-2" style={{ color: '#999999' }}>items this week</div>
        
        {(() => {
          const days = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
          const dailyCounts = momentumMetrics.dailyCounts;
          const maxCount = Math.max(...dailyCounts, 1);
          
          const yPositions = dailyCounts.map(count => 
            Math.max(8, 56 - (count / maxCount) * 48)
          );
          
          const xPositions = [0, 33, 66, 100, 133, 166, 200];
          
          const generateSmoothPath = (xPos, yPos) => {
            if (xPos.length < 2) return '';
            let path = `M ${xPos[0]},${yPos[0]}`;
            for (let i = 0; i < xPos.length - 1; i++) {
              const x1 = xPos[i];
              const y1 = yPos[i];
              const x2 = xPos[i + 1];
              const y2 = yPos[i + 1];
              const cpx1 = x1 + (x2 - x1) / 3;
              const cpx2 = x1 + 2 * (x2 - x1) / 3;
              path += ` C ${cpx1},${y1} ${cpx2},${y2} ${x2},${y2}`;
            }
            return path;
          };
          
          const linePath = generateSmoothPath(xPositions, yPositions);
          const areaPath = linePath + ` L 200,64 L 0,64 Z`;
          
          const yPercents = yPositions.map(y => (y / 64) * 100);
          const xPercents = [0, 16.5, 33, 50, 66.5, 83, 100];
                      
          return (
            <div className="h-16 w-full mb-1 relative pt-5">
              {hoveredDay !== null && (
                <div 
                  className="absolute z-20 pointer-events-none"
                  style={{
                    left: `${xPercents[hoveredDay]}%`,
                    top: `${(yPercents[hoveredDay] * 0.875) + 24 - 32}%`,
                    transform: 'translateX(-50%)',
                  }}
                >
                  <div className="bg-white rounded-lg shadow-lg border border-gray-100 px-3 py-1.5 text-[11px] font-semibold text-gray-900 whitespace-nowrap">
                    {dailyCounts[hoveredDay]} item{dailyCounts[hoveredDay] !== 1 ? 's' : ''}
                  </div>
                </div>
              )}
              <svg width="100%" height="44" viewBox="0 0 200 64" preserveAspectRatio="none" className="w-full">
                <path
                  d={linePath}
                  fill="none"
                  stroke="url(#sparklineStroke)"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity="0.7"
                />
                <path
                  d={areaPath}
                  fill="url(#sparklineGradient)"
                />
                {hoveredDay !== null && (
                  <circle
                    cx={xPositions[hoveredDay]}
                    cy={yPositions[hoveredDay]}
                    r="4"
                    fill="#A78BFA"
                    stroke="white"
                    strokeWidth="2"
                    className="pointer-events-none"
                  />
                )}
                {days.map((day, i) => (
                  <rect
                    key={i}
                    x={i === 0 ? 0 : xPositions[i] - 15}
                    y="0"
                    width="30"
                    height="64"
                    fill="transparent"
                    onMouseEnter={() => onHoverDay(i)}
                    onMouseLeave={() => onHoverDay(null)}
                    className="cursor-pointer"
                  />
                ))}
                <defs>
                  <linearGradient id="sparklineStroke" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#C4B5FD" />
                    <stop offset="50%" stopColor="#A78BFA" />
                    <stop offset="100%" stopColor="#C4B5FD" />
                  </linearGradient>
                  <linearGradient id="sparklineGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#C4B5FD" stopOpacity="0.12" />
                    <stop offset="50%" stopColor="#A78BFA" stopOpacity="0.06" />
                    <stop offset="100%" stopColor="#C4B5FD" stopOpacity="0.01" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
          );
        })()}
        
        <div className="flex items-center justify-between">
          {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((day, i) => (
            <span 
              key={i}
              className="text-[10px] font-medium"
              style={{ 
                color: momentumMetrics.mostActiveDayShort === day ? '#7C3AED' : '#999999'
              }}
            >
              {day}
            </span>
          ))}
        </div>
      </div>
      
      <div className="mt-4 pt-4 border-t" style={{ borderColor: '#F0F0F0' }}>
        {momentumMetrics.hasEnoughData ? (
          <>
            <div className="text-[11px] space-y-3">
              {momentumMetrics.topTwoRisingThemes.length > 0 && (
                <p style={{ color: '#555555' }}>
                  Rising themes: <span className="font-medium" style={{ color: '#111111' }}>{momentumMetrics.topTwoRisingThemes.join(', ')}</span>
                </p>
              )}
              
              <div className="flex flex-wrap gap-x-3 gap-y-1" style={{ color: '#555555' }}>
                <span>Most active <span className="font-medium" style={{ color: '#111111' }}>{momentumMetrics.mostActiveDay}</span></span>
                <span style={{ color: '#E0E0E0' }}>·</span>
                <span>Least active <span className="font-medium" style={{ color: '#111111' }}>{momentumMetrics.leastActiveDay}</span></span>
                <span style={{ color: '#E0E0E0' }}>·</span>
                <span>Peak <span className="font-medium" style={{ color: '#111111' }}>
                  {momentumMetrics.peakUploadHour !== null 
                    ? (() => {
                        const hour = momentumMetrics.peakUploadHour;
                        const period = hour >= 12 ? 'PM' : 'AM';
                        const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                        return `${hour12} ${period}`;
                      })()
                    : '—'}
                </span></span>
              </div>
              
              <p style={{ color: '#555555' }}>
                Change from last week: <span className="font-medium" style={{
                  color: momentumMetrics.weekDeltaText === 'No items yet' 
                    ? '#999999' 
                    : momentumMetrics.weekDeltaText.startsWith('From 0')
                      ? '#7C3AED'
                      : momentumMetrics.weekDeltaPercent >= 0 
                        ? '#7C3AED' 
                        : '#999999'
                }}>
                  {momentumMetrics.weekDeltaText}
                </span>
              </p>
            </div>
            
            {momentumInsight && (
              <p className="mt-3 text-[11px] italic leading-relaxed" style={{ color: '#555555' }}>
                {momentumInsight}
              </p>
            )}
          </>
        ) : (
          <p className="text-[11px] text-center py-2" style={{ color: '#999999' }}>
            Not enough data yet — keep exploring to unlock insights.
          </p>
        )}
      </div>
    </div>
  );
}
